import type { Timestamp } from "@bufbuild/protobuf";
import type { UserBalanceWithFundSource } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import {
  compareBalanceItemPriority,
  compareTimestamps,
} from "../features/pay/utils/balance-sort-utils";
import type { FundSourceFilterCounts } from "./fund-source-filters";

type UserBalanceWithFundSourceAndFilterCounts = UserBalanceWithFundSource &
  FundSourceFilterCounts;

type UserBalanceWithFundSourceAndExpirationDates =
  UserBalanceWithFundSourceAndFilterCounts & {
    expirationDates: Timestamp[];
  };

/**
 * UserBalanceWithFundSourceの配列をソートする関数
 *
 * 引数の各要素は事前に attachFundSourceFilterCounts で
 * storeFilterCount/cityFilterCountを付与しておく必要がある。
 *
 * ソート優先順位:
 * 1. 同じfund_source_idを持つ残高はマージ
 *    - 金額は合算
 *    - 有効期限（expireAt）は近い方（短い方）を採用してソートに使用
 *    - 全ての有効期限はexpirationDatesリストに保持
 * 2. 有効期限が近いものを先に
 * 3. 利用可能店舗の個別選択数が少ないものを先に
 * 4. 特定地域のみ利用可の設定数が少ないものを先に
 * 5. ポイント残高が少ないものを先に
 * 6. priorityWeightが高いものを先に
 */
export function sortBalanceWithFundSources(
  balances: UserBalanceWithFundSourceAndFilterCounts[],
): UserBalanceWithFundSourceAndExpirationDates[] {
  // 同じfund_source_idを持つ残高をマージ
  const mergedMap = new Map<
    string,
    UserBalanceWithFundSourceAndExpirationDates
  >();

  for (const balance of balances) {
    const fundSourceId = balance.fundSource?.id;
    if (!fundSourceId) continue;

    // 全ての有効期限をリストとして保持するための拡張
    const balanceWithExpireDates = {
      ...balance,
      expirationDates: balance.userBalance?.expireAt
        ? [balance.userBalance.expireAt]
        : [],
    } as UserBalanceWithFundSourceAndExpirationDates;

    if (mergedMap.has(fundSourceId)) {
      const existingBalance = mergedMap.get(fundSourceId);
      if (!existingBalance) continue;

      // 金額を合算
      const existingAmount = Number(existingBalance.userBalance?.amount || 0);
      const newAmount = Number(balance.userBalance?.amount || 0);

      if (existingBalance.userBalance) {
        // 型に応じて適切に変換
        existingBalance.userBalance.amount = BigInt(existingAmount + newAmount);
      }

      // 有効期限リストに新しい有効期限を追加（重複チェック付き）
      if (balance.userBalance?.expireAt) {
        const newExpireAt = balance.userBalance.expireAt;
        const isDuplicate = existingBalance.expirationDates.some(
          (existingExpireAt) =>
            compareTimestamps(existingExpireAt, newExpireAt) === 0,
        );
        if (!isDuplicate) {
          existingBalance.expirationDates.push(newExpireAt);
        }
      }

      // ソート用のexpireAtフィールドは期限が短い方（より近い方）を採用
      const existingExpireAt = existingBalance.userBalance?.expireAt;
      const newExpireAt = balance.userBalance?.expireAt;

      // 新しい有効期限が古い（より近い）場合は更新
      if (newExpireAt && compareTimestamps(newExpireAt, existingExpireAt) < 0) {
        if (existingBalance.userBalance) {
          existingBalance.userBalance.expireAt = newExpireAt;
        }
      }
    } else {
      // 新しいバランスをマップに追加
      mergedMap.set(fundSourceId, balanceWithExpireDates);
    }
  }

  // マージした残高の配列を作成
  const mergedBalances = Array.from(mergedMap.values());

  return mergedBalances.sort(compareBalanceItemPriority);
}
